import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive, Router } from '@angular/router';
import { AuthService } from '../../../../core/services/auth.service';
import { ActiveOrgService } from '../../../../core/services/active-org.service';

interface NavItem {
  icon: string;
  label: string;
  route: string;
  description: string;
  section: 'main' | 'monitor';
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Sidebar {
  isOpen = input(true);
  isCollapsed = signal(false);
  collapsedChange = output<boolean>();

  private authService = inject(AuthService);
  private activeOrgService = inject(ActiveOrgService);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Стан спливаючого меню акаунта у футері бічної панелі. */
  accountMenuOpen = signal(false);

  constructor(private router: Router) {
    // Emit collapsed state changes
    effect(() => {
      this.collapsedChange.emit(this.isCollapsed());
    });
  }

  // Базові пункти для всіх

  private readonly baseNavItems: NavItem[] = [
    { label: 'Огляд', route: '/dashboard', icon: 'dashboard', section: 'main', description: 'Огляд системи та глобальний пошук' },
    { label: 'Посадові особи', route: '/officials-management', icon: 'user', section: 'main', description: 'Управління персоналом та посадовими особами' },
    { label: 'Нерухомість', route: '/properties-management', icon: 'home', section: 'main', description: 'Управління об\u02bcєктами нерухомості' },
    { label: 'Земельні ділянки', route: '/land-plots-management', icon: 'land', section: 'main', description: 'Реєстр земель комунальної власності громади' },
    { label: 'Транспорт', route: '/transport-management', icon: 'car', section: 'main', description: 'Управління транспортними засобами' },
  ];

  // Динамічні navItems
  navItems = computed(() => {
    const items = [...this.baseNavItems];

    const isLocalGovernment = this.authService.isLocalGovernment();

    // Порядок «Моніторингу» — як у макеті v3 (`V2_NAV`): Звітність → Перевірки →
    // Аналітика → Оренда → Завдання. Пункти, яких менеджер КП не бачить, просто
    // випадають зі списку, не зсуваючи решту.
    if (isLocalGovernment) {
      items.push({
        label: 'Звітність',
        route: '/financial-reports-registry',
        icon: 'reports',
        section: 'monitor',
        description: 'Реєстр подання фінансової звітності',
      });
    }

    // «Перевірки» й «Оренду» бачать усі: менеджер громади — зведення по всіх КП,
    // менеджер підприємства — те саме у скоупі свого КП. Скоуп визначає бекенд за
    // активною організацією, тож окремих пунктів під ролі тут не треба.
    items.push({
      label: 'Перевірки',
      route: '/inspections',
      icon: 'shield',
      section: 'monitor',
      description: 'Заходи держнагляду та перевірки засновника',
    });

    // Процедури припинення — той самий принцип скоупу: громада бачить усі свої КП,
    // підприємство лише себе. Запускають процедуру з картки юрособи, тут — зведення.
    items.push({
      label: 'Реорганізації',
      route: '/terminations',
      icon: 'termination',
      section: 'monitor',
      description: 'Ліквідація та реорганізація комунальних підприємств',
    });

    // «Благодійність» теж бачать усі: скоуп визначає бекенд за активною організацією —
    // громаді всі КП, підприємству лише своє, як у «Перевірках».
    items.push({
      label: 'Благодійність',
      route: '/charity',
      icon: 'heart',
      section: 'monitor',
      description: 'Отримана допомога та донорські проєкти',
    });

    if (isLocalGovernment) {
      items.push({
        label: 'Аналітика',
        route: '/analytics',
        icon: 'analytics',
        section: 'monitor',
        description: 'Порівняльний аналіз показників комунальних підприємств'
      });
    }

    // Договір оренди заводиться в картці обʼєкта; тут — зведення умов і строків.
    items.push({
      label: 'Оренда',
      route: '/rent',
      icon: 'key',
      section: 'monitor',
      description: 'Орендарі, умови договорів та строки',
    });

    // «Завдання» поки що макет на мок-даних (див. pages/tasks/tasks.mock.ts).
    items.push({
      label: 'Завдання',
      route: '/tasks',
      icon: 'tasks',
      section: 'monitor',
      description: 'Зведений реєстр задач для комунальних підприємств',
    });

    if (isLocalGovernment) {
      // Менеджер ОМС бачить список юр. осіб
      items.splice(1, 0, {
        label: 'Юридичні особи',
        route: '/legal-entities-management',
        icon: 'building',
        section: 'main',
        description: 'Список юридичних осіб'
      });
    } else if (this.authService.isLegalEntity()) {
      // Юр. особа бачить свій профіль
      const legalEntityId = this.authService.getLegalEntityId();
      items.splice(1, 0, {
        label: 'Моя організація',
        route: `/legal-entity-details/${legalEntityId}`,
        icon: 'building',
        section: 'main',
        description: 'Профіль організації'
      });
    }

    return items;
  });

  /** Верхня (основна) група пунктів. */
  mainNavItems = computed(() => this.navItems().filter(i => i.section === 'main'));

  /** Група «Моніторинг» — рендериться разом із заголовком секції, лише коли не порожня. */
  monitorNavItems = computed(() => this.navItems().filter(i => i.section === 'monitor'));

  /** Імʼя користувача для футера. */
  userName = computed(
    () => this.authService.userFull()?.name || this.authService.user()?.name || 'Користувач'
  );

  /** Назва організації під імʼям. */
  orgName = computed(() => {
    const activeOrg = this.activeOrgService.activeOrg();
    if (this.authService.isSupport() && activeOrg) {
      return activeOrg.organizationName;
    }
    const role = this.authService.userFull()?.managementRole;
    return role?.localGovernmentName || role?.legalEntityName || '';
  });

  /** Ініціали для аватара (до двох літер). */
  userInitials = computed(() =>
    this.userName()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part.charAt(0).toUpperCase())
      .join('')
  );

  toggleSidebar() {
    this.isCollapsed.update(val => !val);
    this.accountMenuOpen.set(false);
  }

  toggleAccountMenu() {
    this.accountMenuOpen.update(val => !val);
  }

  closeAccountMenu() {
    this.accountMenuOpen.set(false);
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    if (!this.accountMenuOpen()) return;
    const wrap = this.host.nativeElement.querySelector('.sb-foot-wrap');
    if (wrap && !wrap.contains(event.target as Node)) {
      this.closeAccountMenu();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.closeAccountMenu();
  }

  async logout(): Promise<void> {
    this.closeAccountMenu();
    const result = await this.authService.signOut();
    if (result.success) {
      this.router.navigate(['/login']);
    }
  }

  isActiveRoute(path: string): boolean {
    return this.router.url === path;
  }
}
